import { Money } from "@hubrise/react-components"

import { OptionId, OptionListId } from "shared/hubrise/ids"
import { ICatalog, IOption, IOptionList } from "shared/hubrise/models"
import { findById, removeInArrayById, replaceInArrayById } from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"
import * as PriceOverrides from "./PriceOverrides"
import * as Restrictions from "./Restrictions"
import { generateRefsForItems, GenerateRefsOptions, removeAllRefsFromItems } from "./ref"

/**
 * Return all options in the catalog.
 * @param catalog
 */
export function allOptions(catalog: ICatalog): Array<{ optionList: IOptionList; option: IOption }> {
  return catalog.data.optionLists.flatMap((optionList) => optionList.options.map((option) => ({ optionList, option })))
}

/**
 * Create an empty option.
 */
export function create(catalog: ICatalog): IOption {
  return {
    id: OptionId(catalog.idGenerator()),
    ref: "",
    name: "",
    restrictions: { enabled: true },
    price: Money.zero(catalog.currency),
    priceOverrides: [],
    isDefault: false,
    tags: [],
    nutrition: null,
    weight: null,
    volume: null,
  }
}

/**
 * Delete the option. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param optionListId
 * @param optionId
 */
export function doDelete(catalog: ICatalog, optionListId: OptionListId, optionId: OptionId): ICatalog {
  return updatedCatalog(catalog, {
    optionLists: replaceInArrayById(catalog.data.optionLists, optionListId, (optionList) => ({
      ...optionList,
      options: removeInArrayById(optionList.options, optionId),
    })),
  })
}

/**
 * Return the option matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: OptionId): IOption => {
  for (const optionList of catalog.data.optionLists) {
    const option = findById(optionList.options, id)
    if (option) return option
  }
  throw `Option ${id} not found`
}

/**
 * Return the option matching the id, along with its parent option list.
 * @param catalog
 * @param id
 */
export const findWithOptionListById = (
  catalog: ICatalog,
  id: OptionId,
): { optionList: IOptionList; option: IOption } => {
  for (const optionList of catalog.data.optionLists) {
    const option = findById(optionList.options, id)
    if (option) return { optionList, option }
  }
  throw `Option ${id} not found`
}

/**
 * Returns true if the option should be visible in <Preview>.
 * @param option
 * @param variantRef
 */
export function isVisibleInPreview(option: IOption, variantRef: string | null): boolean {
  return Restrictions.isVisibleInPreview(option.restrictions, variantRef)
}

/**
 * Returns the price of the option for the given variant
 * @param option
 * @param variantRef: the variant ref, or null for the catalog price
 */
export function price(option: IOption, variantRef: string | null): Money {
  return variantRef === null ? option.price : (variantPrice(option, variantRef) ?? option.price)
}

/**
 * Replace an option and return the resulting catalog.
 * @param catalog
 * @param optionListId
 * @param optionId
 * @param fieldName
 * @param value
 */
export function update<T extends keyof IOption>(
  catalog: ICatalog,
  optionListId: OptionListId,
  optionId: OptionId,
  fieldName: T,
  value: IOption[T],
): ICatalog {
  const optionLists = replaceInArrayById(catalog.data.optionLists, optionListId, (optionList) => ({
    ...optionList,
    options: replaceInArrayById(optionList.options, optionId, (option) => ({ ...option, [fieldName]: value })),
  }))
  return updatedCatalog(catalog, { optionLists })
}

/**
 * Remove refs from all options.
 * @param catalog
 */
export function removeAllRefs(catalog: ICatalog): ICatalog {
  return updatedCatalog(catalog, {
    optionLists: catalog.data.optionLists.map((optionList) => ({
      ...optionList,
      options: removeAllRefsFromItems(optionList.options),
    })),
  })
}

/**
 * Generate refs for all options.
 * @param catalog
 * @param options
 */
export function generateRefs(catalog: ICatalog, options: GenerateRefsOptions): ICatalog {
  const counter = { value: 1 }
  return updatedCatalog(catalog, {
    optionLists: catalog.data.optionLists.map((optionList) => ({
      ...optionList,
      options: generateRefsForItems(optionList.options, options, "option", counter),
    })),
  })
}

/**
 * Enable or disable the option. An optional variant ref can be provided.
 * @param catalog
 * @param optionListId
 * @param optionId
 * @param enabled
 * @param variantRef
 */
export function enable(
  catalog: ICatalog,
  optionListId: OptionListId,
  optionId: OptionId,
  enabled: boolean,
  variantRef?: string,
): ICatalog {
  return updatedCatalog(catalog, {
    optionLists: replaceInArrayById(catalog.data.optionLists, optionListId, (optionList) => ({
      ...optionList,
      options: replaceInArrayById(optionList.options, optionId, (option) => ({
        ...option,
        restrictions:
          variantRef === undefined
            ? Restrictions.enable(option.restrictions, enabled)
            : Restrictions.enableVariant(catalog.data.variants, option.restrictions, variantRef, enabled),
      })),
    })),
  })
}

/**
 * Enable or disable all options. An optional variant ref can be provided.
 * @param catalog
 * @param enabled
 * @param variantRef
 */
export function enableAll(catalog: ICatalog, enabled: boolean, variantRef?: string): ICatalog {
  return updatedCatalog(catalog, {
    optionLists: catalog.data.optionLists.map((optionList) => ({
      ...optionList,
      options: optionList.options.map((option) => ({
        ...option,
        restrictions:
          variantRef === undefined
            ? Restrictions.enable(option.restrictions, enabled)
            : Restrictions.enableVariant(catalog.data.variants, option.restrictions, variantRef, enabled),
      })),
    })),
  })
}

/**
 * Return the price override for the given variant, if any.
 * @param option
 * @param variantRef
 */
export function variantPrice(option: IOption, variantRef: string): Money | null {
  return PriceOverrides.variantPrice(option.priceOverrides, variantRef)
}

/**
 * Set the price override for the given variant.
 * @param catalog
 * @param optionListId
 * @param optionId
 * @param variantRef
 * @param price
 */
export function setVariantPrice(
  catalog: ICatalog,
  optionListId: OptionListId,
  optionId: OptionId,
  variantRef: string,
  price: Money | null,
): ICatalog {
  return updatedCatalog(catalog, {
    optionLists: replaceInArrayById(catalog.data.optionLists, optionListId, (optionList) => ({
      ...optionList,
      options: replaceInArrayById(optionList.options, optionId, (option) => ({
        ...option,
        priceOverrides: PriceOverrides.setVariantPrice(option.priceOverrides, variantRef, price),
      })),
    })),
  })
}
